import { useState, useRef, useCallback, useEffect } from 'react';

export const useChatScroll = () => {
  const [userScrolled, setUserScrolled] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollHeightRef = useRef<number>(0);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isTypingRef = useRef(false);
  const lastScrollHeightValueRef = useRef<number>(0);

  const scrollToBottom = useCallback((force: boolean = false) => {
    if (!force && userScrolled) return;
    
    const container = messagesContainerRef.current;
    if (container) {
      const currentScrollHeight = container.scrollHeight;
      
      if (currentScrollHeight === lastScrollHeightValueRef.current && !force) {
        return;
      }
      
      isScrollingRef.current = true;
      lastScrollHeightValueRef.current = currentScrollHeight;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
        isScrollingRef.current = false;
      }, 100);
    }
  }, [userScrolled]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight
      ) < 10;

    if (element.scrollTop < lastScrollHeightRef.current && element.scrollTop > 0) {
      setUserScrolled(true);
    }

    if (isAtBottom || element.scrollTop === 0) {
      setUserScrolled(false);
    }

    lastScrollHeightRef.current = element.scrollTop;
  }, []);

  const resetUserScrolled = useCallback(() => {
    setUserScrolled(false);
  }, []);

  const setTyping = useCallback((isTyping: boolean) => {
    isTypingRef.current = isTyping;
    if (isTyping) {
      scrollToBottom(true);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return {
    userScrolled,
    messagesContainerRef,
    scrollToBottom,
    handleScroll,
    resetUserScrolled,
    setTyping,
  };
}; 